import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, Modal, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

/**
 * 기간 변경 확인 — 지금 상태와 바뀔 상태를 나란히 보여주고 저장 여부를 묻는다.
 *
 * 기간을 바꾸면 상태(이용중/사용예정/만료)가 함께 뒤집히는데, 저장하기 전에는 그게 보이지 않았다.
 * 특히 종료일을 과거로 옮겨 만료가 되는 경우 되돌리려면 다시 기간을 고쳐야 하므로 미리 알린다.
 *
 * 뒤 이용권을 밀어야 하는 경우도 같은 창에서 함께 보여준다 — 확인 단계를 둘로 나누지 않는다.
 * 밀리는 이용권도 같은 카드 형식으로 그려서, 무엇이 어디로 가는지 한눈에 대조되게 한다.
 */

// 카드 배지 팔레트와 동일 (LockerSpotCard)
const STATUS_STYLE = {
  current: { text: '이용중', color: '#4AB3BC', bg: 'rgba(74,179,188,0.12)' },
  upcoming: { text: '사용예정', color: '#55C982', bg: 'rgba(85,201,130,0.12)' },
  expired: { text: '만료', color: '#737373', bg: '#F0F0F0' },
  stopped: { text: '중지', color: '#EE735A', bg: 'rgba(238,115,90,0.12)' },
};

// 중지는 기간과 무관한 상태라 날짜보다 먼저 본다.
// 중지된 이용권의 기간을 바꿔도 여전히 중지인데 '이용중' 으로 보이면 오해를 부른다.
const statusOf = (start, end, status) => {
  const today = moment().startOf('day');
  if (status === 'STOPPED') return 'stopped';
  if (!start || !end) return 'current';
  if (moment(end).startOf('day').isBefore(today, 'day')) return 'expired';
  if (moment(start).startOf('day').isAfter(today, 'day')) return 'upcoming';
  return 'current';
};

const fmt = d => (d ? moment(d).format('YYYY.MM.DD') : '-');
const spanOf = (start, end) =>
  start && end ? moment(end).startOf('day').diff(moment(start).startOf('day'), 'days') + 1 : 0;

const StateCard = ({ label, start, end, status, dim, compact }) => {
  const { t } = useTranslation();
  const style = STATUS_STYLE[statusOf(start, end, status)];
  return (
    <Box
      sx={{
        flex: compact ? '1 1 195px' : '1 1 225px', minWidth: 0,
        p: compact ? '8px 10px' : '12px 14px', borderRadius: compact ? '8px' : '10px',
        border: dim ? '1px solid #e5e5e5' : `1px solid ${style.color}`,
        background: dim ? '#FAFAFA' : '#fff',
        opacity: dim ? 0.85 : 1,
      }}>
      {/* 윗줄 — 왼쪽에 어느 시점인지, 오른쪽 끝에 총 일수 */}
      <Box
        sx={{
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
          gap: '6px', mb: compact ? '5px' : '7px',
        }}>
        <Typography sx={{ fontSize: compact ? 11 : 11.5, fontWeight: 600, color: '#8C8C8C' }}>
          {t(label)}
        </Typography>
        <Typography
          sx={{ fontSize: compact ? 11 : 11.5, fontWeight: 600, color: '#737373', whiteSpace: 'nowrap' }}>
          {t('총 {{days}}일', { days: spanOf(start, end) })}
        </Typography>
      </Box>

      {/* 아랫줄 — 상태 배지와 기간을 한 줄에 */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
        <Box
          component="span"
          sx={{
            flexShrink: 0, fontSize: compact ? 10 : 10.5, fontWeight: 700, borderRadius: '5px',
            p: compact ? '1px 6px' : '2px 7px', color: style.color, background: style.bg,
          }}>
          {t(style.text)}
        </Box>
        <Typography
          sx={{
            fontSize: compact ? 12 : 13.5, fontWeight: 700, color: '#262626', whiteSpace: 'nowrap',
          }}>
          {fmt(start)} ~ {fmt(end)}
        </Typography>
      </Box>
    </Box>
  );
};

const ChangeBlock = ({ productName, before = {}, after = {} }) => {
  const { t } = useTranslation();
  const beforeStatus = statusOf(before.startDate, before.expireDate, before.status);
  const afterStatus = statusOf(after.startDate, after.expireDate, after.status);
  const statusChanged = beforeStatus !== afterStatus;
  const becomesExpired = afterStatus === 'expired' && beforeStatus !== 'expired';

  return (
    <Box sx={{ mb: '16px' }}>
      {productName && (
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#262626', mb: '10px' }}>
          {productName}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', gap: '10px' }}>
        <StateCard
          label="현재"
          start={before.startDate}
          end={before.expireDate}
          status={before.status}
          dim
        />
        <Box
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20, color: '#8C8C8C', flexShrink: 0, width: 24,
          }}>
          →
        </Box>
        <StateCard label="변경 후" start={after.startDate} end={after.expireDate} status={after.status} />
      </Box>

      {statusChanged && (
        <Box
          sx={{
            mt: '12px', p: '12px 14px', borderRadius: '8px',
            background: becomesExpired ? 'rgba(238,115,90,0.08)' : '#F5F7F8',
            border: `1px solid ${becomesExpired ? 'rgba(238,115,90,0.35)' : '#E3E8E9'}`,
          }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#262626' }}>
            {t('상태가 {{from}} → {{to}} 로 바뀝니다', {
              from: t(STATUS_STYLE[beforeStatus].text),
              to: t(STATUS_STYLE[afterStatus].text),
            })}
          </Typography>
          {becomesExpired && (
            <Typography sx={{ fontSize: 12.5, color: '#B4503A', mt: '4px', lineHeight: 1.5 }}>
              {t('만료가 되면 사용 중 목록에서 빠지고, 같은 자리에 유효한 이용권이 있으면 자리에서도 빠집니다.')}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

const PeriodChangePreviewModal = ({
  open,
  onClose,
  onConfirm,
  isSubmitting = false,
  productName,
  before = {},
  after = {},
  items,
  shifted = [],
}) => {
  const { t } = useTranslation();
  // 한 번에 여러 이용권 기간을 고치는 화면(라커관리)도 같은 창을 쓴다.
  const changes = items?.length ? items : [{ productName, before, after }];
  // 밀리는 이용권이 여럿이면 세로로 쌓지 않고 한 건씩 넘겨 본다 — 창이 길어지면 저장 버튼이 밀려난다.
  const [shiftIndex, setShiftIndex] = useState(0);
  useEffect(() => {
    if (open) setShiftIndex(0);
  }, [open, shifted.length]);
  const current = shifted[Math.min(shiftIndex, Math.max(shifted.length - 1, 0))];

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="period-preview-title">
      <Box
        sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: 'min(560px, calc(100vw - 32px))', maxHeight: 'calc(100vh - 64px)', overflowY: 'auto',
          bgcolor: '#fff', borderRadius: '12px', boxShadow: 24, outline: 'none',
        }}>
        <Box
          id="period-preview-title"
          sx={{
            display: 'flex', alignItems: 'center', height: 48, px: '20px',
            bgcolor: '#262626', color: '#fff', borderRadius: '12px 12px 0 0',
            fontSize: 15, fontWeight: 600,
          }}>
          {t('기간 변경 확인')}
        </Box>

        <Box sx={{ p: '20px' }}>
          {changes.map((c, idx) => (
            <ChangeBlock
              key={c.key ?? idx}
              productName={c.productName}
              before={c.before}
              after={c.after}
            />
          ))}

          {shifted.length > 0 && (
            <Box sx={{ mt: '4px', pt: '14px', borderTop: '1px solid #ECECEC' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#262626' }}>
                  {t('함께 밀리는 이용권 {{count}}건', { count: shifted.length })}
                </Typography>
                {shifted.length > 1 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
                    <IconButton
                      size="small"
                      aria-label={t('이전')}
                      disabled={shiftIndex === 0}
                      onClick={() => setShiftIndex(i => Math.max(0, i - 1))}>
                      <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                    <Typography sx={{ fontSize: 12, color: '#737373', minWidth: 34, textAlign: 'center' }}>
                      {shiftIndex + 1} / {shifted.length}
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label={t('다음')}
                      disabled={shiftIndex >= shifted.length - 1}
                      onClick={() => setShiftIndex(i => Math.min(shifted.length - 1, i + 1))}>
                      <ChevronRightIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
              <Typography sx={{ fontSize: 12.5, color: '#737373', mt: '2px', lineHeight: 1.5 }}>
                {t('같은 자리는 기간이 겹칠 수 없어 이용 일수를 유지한 채 뒤로 미룹니다.')}
              </Typography>

              {current && (
                <Box
                  sx={{
                    mt: '10px', p: '10px 12px', borderRadius: '10px',
                    background: '#FAFBFC', border: '1px solid #E3E8E9',
                  }}>
                  <Typography
                    sx={{
                      fontSize: 12.5, fontWeight: 600, color: '#262626', mb: '6px',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                    {current.productName || t('라커 이용권')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', gap: '6px' }}>
                    <StateCard
                      label="현재"
                      start={current.before?.startDate}
                      end={current.before?.expireDate}
                      dim
                      compact
                    />
                    <Box
                      sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 16, color: '#8C8C8C', flexShrink: 0, width: 18,
                      }}>
                      →
                    </Box>
                    <StateCard
                      label="밀린 뒤"
                      start={current.after?.startDate}
                      end={current.after?.expireDate}
                      compact
                    />
                  </Box>
                </Box>
              )}
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', mt: '20px' }}>
            <Button
              onClick={onClose}
              disabled={isSubmitting}
              sx={{
                height: 42, px: '22px', minWidth: 92, borderRadius: '8px',
                fontSize: 14.5, color: '#262626', bgcolor: '#e5e5e5',
                '&:hover': { bgcolor: '#d9d9d9' },
              }}>
              {t('취소')}
            </Button>
            <Button
              variant="contained"
              onClick={onConfirm}
              disabled={isSubmitting}
              sx={{
                height: 42, px: '22px', minWidth: 92, borderRadius: '8px',
                fontSize: 14.5, fontWeight: 600, bgcolor: '#0F7F85',
                '&:hover': { bgcolor: '#0D6A6F' },
              }}>
              {isSubmitting ? t('저장 중...') : t('저장')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default PeriodChangePreviewModal;
